import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useCurrency } from "@/lib/currency";

export const CartDrawer = () => {
  const { cart, drawerOpen, closeDrawer, updateItem, removeItem } = useCart();
  const { formatPrice } = useCurrency();

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="cart-backdrop"
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 z-[61] h-full w-full sm:w-[460px] bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <header className="px-6 py-5 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium">Your cart</div>
            <h2 className="font-heading text-2xl font-black tracking-tight">{cart.total_qty} item{cart.total_qty !== 1 ? "s" : ""}</h2>
          </div>
          <button onClick={closeDrawer} className="w-9 h-9 flex items-center justify-center hover:bg-neutral-100" data-testid="cart-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Wholesale + MOQ info banner (visible in every cart state) */}
        <div className="px-6 py-3 bg-neutral-900 text-white border-b border-neutral-800" data-testid="cart-wholesale-banner">
          <div className="flex items-start gap-2.5">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase bg-emerald-500 text-black px-1.5 py-0.5 shrink-0 mt-0.5">Wholesale</span>
            <div className="text-[11px] leading-snug text-neutral-200">
              All prices shown are <span className="font-bold text-white">wholesale B2B rates</span>.
              To unlock <span className="font-bold text-emerald-300">better unit prices &amp; discounted shipping</span>,
              our minimum order is <span className="font-bold text-white">5 units</span> per checkout.
            </div>
          </div>
        </div>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-empty">
            <p className="text-sm text-neutral-500 mb-4">Your cart is empty.</p>
            <Link onClick={closeDrawer} to="/" className="border-b border-black pb-0.5 text-sm font-medium">Continue shopping →</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-neutral-100" data-testid="cart-items">
              {cart.items.map((i) => (
                <div key={i.product_id} className="flex gap-3 py-4" data-testid={`cart-item-${i.product_id}`}>
                  <img src={i.image} alt={i.name} loading="lazy" decoding="async" className="w-16 h-16 object-cover bg-neutral-100" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{i.name}</div>
                    <div className="text-xs text-neutral-500">{formatPrice(i.unit_price)} {i.discount_pct ? <span className="ml-1 text-emerald-700">-{i.discount_pct}%</span> : null}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="inline-flex items-center border border-neutral-300">
                        <button onClick={() => updateItem(i.product_id, Math.max(1, i.qty - 1))} className="w-7 h-7 flex items-center justify-center hover:bg-neutral-100" data-testid={`cart-dec-${i.product_id}`}><Minus className="w-3 h-3" /></button>
                        <span className="w-8 text-center text-sm font-bold">{i.qty}</span>
                        <button onClick={() => updateItem(i.product_id, i.qty + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-neutral-100" data-testid={`cart-inc-${i.product_id}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <button onClick={() => removeItem(i.product_id)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`cart-remove-${i.product_id}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-sm font-bold whitespace-nowrap">{formatPrice(i.line_total)}</div>
                </div>
              ))}
            </div>

            <footer className="border-t border-neutral-200 p-6 space-y-3" data-testid="cart-footer">
              {!cart.moq_reached && (
                <div
                  className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2.5 text-xs leading-snug flex items-start gap-2"
                  data-testid="cart-moq-banner"
                >
                  <span className="text-base leading-none">⚠️</span>
                  <div className="flex-1">
                    Add <span className="font-bold">{cart.moq_remaining} more item{cart.moq_remaining === 1 ? "" : "s"}</span> to unlock checkout.{" "}
                    <span className="font-medium text-amber-700">Minimum order: {cart.moq} units.</span>
                  </div>
                </div>
              )}
              {(() => {
                const fullSubtotal = cart.items.reduce((s, i) => s + i.unit_price * i.qty, 0);
                const savings = fullSubtotal - cart.subtotal;
                const hasDiscount = cart.cart_discount_pct > 0 && savings > 0.005;
                return (
                  <div className="pt-1 space-y-1" data-testid="cart-subtotals">
                    {hasDiscount && (
                      <div className="flex items-baseline justify-between" data-testid="cart-subtotal-full">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium">Subtotal</span>
                        <span className="text-base font-medium text-neutral-400 line-through">
                          {formatPrice(fullSubtotal)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-baseline justify-between" data-testid="cart-subtotal-final">
                      <span className="text-[10px] tracking-[0.3em] uppercase font-bold">
                        {hasDiscount
                          ? `Subtotal with ${cart.cart_discount_pct}% off`
                          : "Subtotal"}
                      </span>
                      <span className="font-heading text-3xl font-black tracking-tight">
                        {formatPrice(cart.subtotal)}
                      </span>
                    </div>
                    {hasDiscount && (
                      <div className="text-[11px] text-emerald-700 font-bold text-right" data-testid="cart-savings">
                        You save ${savings.toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })()}
              {cart.moq_reached ? (
                <Link
                  to="/checkout"
                  onClick={closeDrawer}
                  className="block w-full h-12 bg-black text-white text-sm font-medium hover:bg-neutral-800 inline-flex items-center justify-center"
                  data-testid="cart-checkout"
                >
                  Checkout
                </Link>
              ) : (
                <button
                  type="button"
                  disabled
                  className="block w-full h-12 bg-neutral-200 text-neutral-500 text-sm font-medium cursor-not-allowed inline-flex items-center justify-center"
                  data-testid="cart-checkout-disabled"
                  title={`Add ${cart.moq_remaining} more item${cart.moq_remaining === 1 ? "" : "s"} to checkout`}
                >
                  Add {cart.moq_remaining} more to checkout
                </button>
              )}
            </footer>
          </>
        )}
      </aside>
    </>
  );
};
